/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentType } from '@angular/cdk/portal';
import { DOCUMENT, inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ConfettiService } from '@rma/public/generic/ui/confetti';
import { ConfirmActionComponent, ConfirmActionData } from '@rma/public/generic/ui/confirm-action-modal';

export type RmaModalRef<T, R = any> = MatDialogRef<T, R>;
export type RmaModalConfig<D> = MatDialogConfig<D>;

@Injectable()
export class RmaModalService {
  private readonly confettiService = inject(ConfettiService);
  private readonly dialogService = inject(MatDialog);
  private readonly document = inject<Document>(DOCUMENT);

  public open<T, D = any, R = any>(componentClass: ComponentType<T>, config?: RmaModalConfig<D>): RmaModalRef<T, R> {
    if (config) {
      return this.dialogService.open<T, D>(componentClass, config);
    }
    return this.dialogService.open<T>(componentClass);
  }

  public openConfirmAction<D, R = any>(data: ConfirmActionData<D>): RmaModalRef<ConfirmActionComponent, R> {
    return this.open(ConfirmActionComponent, {
      data,
      panelClass: 'rma-confirm-action-panel',
    });
  }

  public openCelebrationFullScreen<T, D = any, R = any>(
    componentClass: ComponentType<T>,
    baseConfig: RmaModalConfig<D> = {},
  ): RmaModalRef<T, R> {
    this.confettiService.start(2500);
    return this.openFullScreen(componentClass, baseConfig);
  }

  public openCelebrationFullScreenMobile<T, D = any, R = any>(
    componentClass: ComponentType<T>,
    baseConfig: RmaModalConfig<D> = {},
  ): RmaModalRef<T, R> {
    this.confettiService.start(2500);
    return this.openFullScreenOnMobileOnly(componentClass, baseConfig);
  }

  public openFullScreen<T, D = any, R = any>(componentClass: ComponentType<T>, baseConfig: RmaModalConfig<D> = {}): RmaModalRef<T, R> {
    const bodyClasses = 'no-scrollbar';
    this.document.body.classList.add(bodyClasses);
    const config = this.mergeConfigs<D>(baseConfig, {
      panelClass: 'rma-fullscreen-panel',
    });
    const ref = this.open<T, D>(componentClass, config);
    ref.afterClosed().subscribe(() => this.document.body.classList.remove(bodyClasses));
    return ref;
  }

  public openFullScreenOnMobileOnly<T, D = any, R = any>(
    componentClass: ComponentType<T>,
    baseConfig: RmaModalConfig<D> = {},
  ): RmaModalRef<T, R> {
    const bodyClasses = ['no-scrollbar', 'md:scrollbar'];
    bodyClasses.forEach((c) => this.document.body.classList.add(c));
    const config = this.mergeConfigs<D>(baseConfig, {
      maxWidth: '56.25rem',
      panelClass: 'rma-fullscreen-mobile-panel',
    });
    const ref = this.open<T, D>(componentClass, config);
    ref.afterClosed().subscribe(() => bodyClasses.forEach((c) => this.document.body.classList.remove(c)));
    return ref;
  }

  private mergeConfigs<D>(base: RmaModalConfig<any>, overrides: RmaModalConfig<D>): RmaModalConfig<D> {
    // SAFETY(legacy): unreviewed, pre-dates this rule.
    const panelClass = [base.panelClass, overrides.panelClass].filter((p) => Boolean(p)) as string[];
    return { ...base, ...overrides, panelClass };
  }
}
